import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Payment.css';
import { getPaymentHistory, getPaymentDetail } from '../utils/api';

const Payment = () => {
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10
  });

  // API에서 결제 내역 가져오기
  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // localStorage에서 회사 코드 가져오기
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const companyCode = userInfo.middleCompanyCode;
        
        if (!companyCode) {
          throw new Error('회사 코드를 찾을 수 없습니다.');
        }
        
        console.log('회사 코드:', companyCode);
        
        // 결제 내역 API 호출
        const response = await getPaymentHistory(pagination.currentPage, pagination.limit, companyCode);
        console.log('결제 내역 API 응답:', response);
        
        if (response && response.status === 'success') {
          // 페이지네이션 정보 설정
          if (response.pagination) {
            setPagination({
              currentPage: response.pagination.current_page || 1,
              totalPages: response.pagination.total_pages || 1,
              totalItems: response.pagination.total_items || 0,
              limit: response.pagination.limit || 10
            });
          }
          
          // API 응답에서 직접 결제 내역 데이터 사용
          if (response.data && Array.isArray(response.data)) {
            setPaymentData(response.data);
            console.log('결제 내역 데이터:', response.data);
          } else {
            console.log('결제 내역이 없거나 예상된 형식이 아닙니다.');
            setPaymentData([]);
          }
        } else {
          console.error('API 응답 형식 오류:', response);
          setError('결제 내역을 불러오는데 실패했습니다.');
          setPaymentData([]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('결제 내역 로딩 오류:', err);
        setError('결제 내역을 불러오는 중 오류가 발생했습니다: ' + err.message);
        setLoading(false);
      }
    };
    
    fetchPaymentData();
  }, [pagination.currentPage, pagination.limit]);

  // 날짜를 "YYYY-MM-DD" 형식으로 변환
  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };
  
  // 날짜와 시간을 "YYYY-MM-DD HH:MM" 형식으로 변환
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // 플랜 클릭 처리 함수
  const handlePlanClick = async (paymentId) => {
    console.log(`${paymentId} 상세 정보 확인`);
    
    try {
      // 결제 상세 정보 API 호출
      const detailResponse = await getPaymentDetail(paymentId);
      
      // 콘솔에 결과 출력
      console.log('결제 상세 정보 응답:', detailResponse);
      
      if (detailResponse && detailResponse.status === 'success') {
        console.log('결제 상세 데이터:', detailResponse.data);
        // 결제 상세 페이지로 이동
        navigate(`/dashboard/payment/${paymentId}`);
      } else {
        console.error('결제 상세 정보를 가져오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('결제 상세 정보 로드 오류:', error);
    }
  };

  // 환불 규정 확인하기 버튼 클릭 핸들러
  const handleRefundPolicy = () => {
    // 새 탭이 아닌 현재 페이지에서 이동
    navigate('/dashboard/refund-policy/68149e7ffa110788f88d6b03');
  };

  // 금액 포맷팅 함수
  const formatCurrency = (amount) => {
    return `₩ ${amount.toLocaleString()}`;
  };
  
  // 페이지 변경 처리
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination({
        ...pagination,
        currentPage: newPage
      });
    }
  };

  if (loading) {
    return <div className="paymentContainer">
      <div className="paymentHeader">
      <h1 className="pageTitle">결제내역</h1>
        <button className="refund-policy-button" onClick={handleRefundPolicy}>
          <span>환불 규정 확인하기</span>
          <span className="arrow-icon">&gt;</span>
        </button>
      </div>
      <div className="loadingMessage">결제 내역을 불러오는 중...</div>
    </div>;
  }
  
  if (error) {
    return <div className="paymentContainer">
      <div className="paymentHeader">
      <h1 className="pageTitle">결제내역</h1>
        <button className="refund-policy-button" onClick={handleRefundPolicy}>
          <span>환불 규정 확인하기</span>
          <span className="arrow-icon">&gt;</span>
        </button>
      </div>
      <div className="errorMessage">{error}</div>
      <button 
        className="retryButton"
        onClick={() => window.location.reload()}
      >
        다시 시도
      </button>
    </div>;
  }
  
  // 결제 내역이 없는 경우
  if (paymentData.length === 0) {
    return <div className="paymentContainer">
      <div className="paymentHeader">
      <h1 className="pageTitle">결제내역</h1>
        <button className="refund-policy-button" onClick={handleRefundPolicy}>
          <span>환불 규정 확인하기</span>
          <span className="arrow-icon">&gt;</span>
        </button>
      </div>
      <div className="emptyMessage">결제 내역이 없습니다.</div>
    </div>;
  }

  return (
    <div className="paymentContainer">
      <div className="paymentHeader">
      <h1 className="pageTitle">결제내역</h1>
        <button className="refund-policy-button" onClick={handleRefundPolicy}>
          <span>환불 규정 확인하기</span>
          <span className="arrow-icon">&gt;</span>
        </button>
      </div>
      
      <div className="paymentTableContainer">
        <table className="paymentTable">
          <thead>
            <tr>
              <th>날짜</th>
              <th>플랜</th>
              <th>종류</th>
              <th>결제 방법</th>
              <th>금액</th>
              <th>플랜 적용 기간</th>
            </tr>
          </thead>
          <tbody>
            {paymentData.map((payment) => (
              <tr key={payment.id}>
                <td>{payment.date}</td>
                <td>
                  <button 
                    className="planLink"
                    onClick={() => handlePlanClick(payment.id)}
                  >
                    {payment.plan}
                  </button>
                </td>
                <td className={
                  payment.type === "환불" 
                    ? "refundType" 
                    : payment.type === "연장"
                      ? "extensionType"
                      : "purchaseType"
                }>
                  {payment.type}
                </td>
                <td>{payment.payment_method}</td>
                <td>{formatCurrency(payment.amount)}</td>
                <td>{payment.period}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* 페이지네이션 UI */}
      {pagination.totalPages > 1 && (
        <div className="paginationContainer">
          <button 
            className="pageButton"
            onClick={() => handlePageChange(1)}
            disabled={pagination.currentPage === 1}
          >
            처음
          </button>
          <button 
            className="pageButton"
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
          >
            이전
          </button>
          
          <span className="pageInfo">
            {pagination.currentPage} / {pagination.totalPages} 페이지
          </span>
          
          <button 
            className="pageButton"
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
          >
            다음
          </button>
          <button 
            className="pageButton"
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={pagination.currentPage === pagination.totalPages}
          >
            마지막
          </button>
        </div>
      )}
    </div>
  );
};

export default Payment; 